import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useStore } from "@/lib/store";
import { api } from "@/lib/api";

export default function PaymentSuccess() {
  const { t } = useLang();
  const { clear } = useStore();
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", order: null });
  const cleared = useRef(false);

  useEffect(() => {
    if (!sid) return setState({ phase: "failed" });
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          if (!cleared.current) { clear(); cleared.current = true; }
          return setState({ phase: "paid", order: data.order_number });
        }
        if (["expired", "failed"].includes(data.payment_status)) return setState({ phase: "failed" });
      } catch (e) { /* keep polling */ }
      if (tries >= 20) return setState({ phase: "pending" });
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  return (
    <div className="mx-auto flex max-w-xl flex-col items-center px-5 py-24 text-center" data-testid="payment-success-page">
      {state.phase === "checking" && (<><Loader2 className="h-12 w-12 animate-spin text-gold" /><p className="mt-6 text-lg font-bold" data-testid="payment-checking">{t("success.checking")}</p></>)}
      {state.phase === "paid" && (
        <>
          <CheckCircle2 className="h-14 w-14 text-emerald-400" />
          <h1 className="mt-6 font-display text-3xl sm:text-4xl">{t("success.title")}</h1>
          <div className="panel mt-8 w-full p-6">
            <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-slate-400">{t("success.order_no")}</p>
            <p className="mt-2 font-mono-num text-3xl font-bold text-gold" data-testid="order-number">{state.order}</p>
          </div>
          <p className="mt-5 text-slate-400">{t("success.keep")}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/track" className="btn-gold" data-testid="success-track-btn">{t("success.track")}</Link>
            <Link to="/" className="btn-ghost" data-testid="success-home-btn">{t("success.home")}</Link>
          </div>
        </>
      )}
      {(state.phase === "pending" || state.phase === "failed") && (
        <>
          <AlertTriangle className="h-12 w-12 text-amber-400" />
          <p className="mt-6 text-lg font-bold" data-testid="payment-status-msg">{t(state.phase === "pending" ? "success.pending" : "success.failed")}</p>
          <Link to="/cart" className="btn-ghost mt-8" data-testid="success-cart-btn">{t("cancel.back")}</Link>
        </>
      )}
    </div>
  );
}
